import * as React from 'react'
import { usePrevious } from 'reactUtils'
import { StatsServiceContext } from 'http/context'
import { format } from 'date-fns'
import { ChartDataLoader } from 'uiComponents/loaders'
import { MessageKind } from 'uiComponents/messages'
import { getComparisonColorFromPercentString, chartRowsGap } from 'reports/helpers'
import { DataNumber } from 'uiComponents/charts/dataNumber'
import { ChartRow, ChartContainer } from 'uiComponents/charts/styleComponents'
import { QueryConfig } from 'reports/queryGenerator'
import { withNavigation } from 'hocs'
import { match as RouteMatch } from 'react-router-dom'
import { Navigation } from 'navigation'
import { DateRange, DateRangeName } from 'dateRanges'
import { areDateRangeDatesEqual, getComparisonCopyMap } from 'reports/helpers'
import { abbreviateNumber } from 'utils'
import { isBefore, startOfToday, differenceInCalendarDays } from 'date-fns'
import { Filters } from 'uiComponents/filter/schema'
import { ReportTimeIdentifierType } from 'reports/schema'
import isEqual from 'lodash/isEqual'
import { t, useTranslation } from 'translations'

const queryConfig: QueryConfig = {
    querySetName: 'GeneralKPIs',
    variablesConfig: [
        { name: 'accountSlug', type: 'String' },
        { name: 'dateFrom', type: 'Date' },
        { name: 'dateTo', type: 'Date' },
        { name: 'comparisonType', type: 'ComparisonTypes' },
        { name: 'timeFilter', type: 'StatsTimeFilter' },
        { name: 'delayType', type: 'DelayTypes' },
        { name: 'delay', type: 'Int' },
        { name: 'filters', type: '[FilterDictionary]' },
        { name: 'timeIdentifierType', type: 'TimeIdentifierType' },
    ],
    queries: [
        {
            name: 'revenue',
            type: 'comparisonMetrics',
            configVariables: [
                'accountSlug',
                'dateFrom',
                'dateTo',
                'comparisonType',
                'timeFilter',
                'delayType',
                'delay',
                'filters',
                'timeIdentifierType',
            ],
            customVariables: [{ name: 'metric', customValue: 'revenue' }],
            presetResult: 'totalsComparison',
        },
        {
            name: 'avgCartSize',
            type: 'comparisonMetrics',
            configVariables: [
                'accountSlug',
                'dateFrom',
                'dateTo',
                'comparisonType',
                'timeFilter',
                'delayType',
                'delay',
                'filters',
                'timeIdentifierType',
            ],
            customVariables: [{ name: 'metric', customValue: 'avg_cart_size' }],
            presetResult: 'totalsComparison',
        },
        {
            name: 'avgCartValue',
            type: 'comparisonMetrics',
            configVariables: [
                'accountSlug',
                'dateFrom',
                'dateTo',
                'comparisonType',
                'timeFilter',
                'delayType',
                'delay',
                'filters',
                'timeIdentifierType',
            ],
            customVariables: [{ name: 'metric', customValue: 'avg_order_size' }],
            presetResult: 'totalsComparison',
        },
        {
            name: 'checkoutConvRate',
            type: 'comparisonMetrics',
            configVariables: [
                'accountSlug',
                'dateFrom',
                'dateTo',
                'comparisonType',
                'timeFilter',
                'delayType',
                'delay',
                'filters',
                'timeIdentifierType',
            ],
            customVariables: [{ name: 'metric', customValue: 'checkout_conversion_rate' }],
            presetResult: 'totalsComparison',
        },
        {
            name: 'websiteConvRate',
            type: 'comparisonMetrics',
            configVariables: [
                'accountSlug',
                'dateFrom',
                'dateTo',
                'comparisonType',
                'timeFilter',
                'delayType',
                'delay',
                'filters',
                'timeIdentifierType',
            ],
            customVariables: [{ name: 'metric', customValue: 'website_conversion_rate' }],
            presetResult: 'totalsComparison',
        },
    ],
}

const getInfotipContentMap = () => ({
    avgCartSize: {
        pageName: t('reports.sales.infotips.avgCartSize.pageName'),
        itemName: t('reports.sales.infotips.avgCartSize.itemName'),
        more: t('reports.comparisonCopy.more'),
        less: t('reports.comparisonCopy.less'),
        metricText: t('reports.sales.infotips.avgCartSize.metricText'),
    },
    revenue: {
        pageName: t('reports.sales.infotips.revenue.pageName'),
        itemName: t('reports.sales.infotips.revenue.itemName'),
        more: t('reports.comparisonCopy.more'),
        less: t('reports.comparisonCopy.less'),
        metricText: t('reports.sales.infotips.revenue.metricText'),
    },
    avgCartValue: {
        pageName: t('reports.sales.infotips.avgCartValue.pageName'),
        itemName: t('reports.sales.infotips.avgCartValue.itemName'),
        more: t('reports.comparisonCopy.higher'),
        less: t('reports.comparisonCopy.lower'),
        metricText: t('reports.sales.infotips.avgCartValue.metricText'),
    },
    checkoutConvRate: {
        pageName: t('reports.sales.infotips.checkoutConvRate.pageName'),
        itemName: t('reports.sales.infotips.checkoutConvRate.itemName'),
        more: t('reports.comparisonCopy.higher'),
        less: t('reports.comparisonCopy.lower'),
        metricText: t('reports.sales.infotips.checkoutConvRate.metricText'),
    },
    websiteConvRate: {
        pageName: t('reports.sales.infotips.websiteConvRate.pageName'),
        itemName: t('reports.sales.infotips.websiteConvRate.itemName'),
        more: t('reports.comparisonCopy.higher'),
        less: t('reports.comparisonCopy.lower'),
        metricText: t('reports.sales.infotips.websiteConvRate.metricText'),
    },
})

interface SingleDataItemsProps {
    accountSlug: string
    navigation: Navigation
    match: RouteMatch<{}>
    dateRange: DateRange
    timeIdentifierType: ReportTimeIdentifierType
    filters: Filters[]
    replaceMessages: (id: string, status: MessageKind, text: string) => void
}

function SingleDataItems(props: SingleDataItemsProps) {
    const { t } = useTranslation()
    const infotipContentMap = getInfotipContentMap()
    const _isMounted = React.useRef(false)
    const _lastRequest = React.useRef<number>()
    const statsService = React.useContext(StatsServiceContext)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [timePeriod, setTimePeriod] = React.useState<DateRangeName>('yesterday')
    const [timePeriodLength, setTimePeriodLength] = React.useState<number>(0)
    const [avgCartSize, setAvgCartSize] = React.useState<string>('0')
    const [revenue, setRevenue] = React.useState<string>('0')
    const [avgCartValue, setAvgCartValue] = React.useState<string>('0')
    const [checkoutConvRate, setCheckoutConvRate] = React.useState<string>('0')
    const [websiteConvRate, setWebsiteConvRate] = React.useState<string>('0')
    const [comparisonCartSize, setComparisonCartSize] = React.useState<string>('-')
    const [comparisonRevenue, setComparisonRevenue] = React.useState<string>('-')
    const [comparisonCartValue, setComparisonCartValue] = React.useState<string>('-')
    const [comparisonCoConvRate, setComparisonCoConvRate] = React.useState<string>('-')
    const [comparisonWebConvRate, setComparisonWebConvRate] = React.useState<string>('-')

    const prevAccountSlug = usePrevious(props.accountSlug)
    const prevDateRange = usePrevious(props.dateRange)
    const prevFilters = usePrevious(props.filters)
    const prevTimeIdentifierType = usePrevious(props.timeIdentifierType)
    React.useEffect(() => {
        _isMounted.current = true
        if (
            prevAccountSlug !== props.accountSlug ||
            !areDateRangeDatesEqual(prevDateRange, props.dateRange) ||
            !isEqual(prevFilters, props.filters) ||
            prevTimeIdentifierType !== props.timeIdentifierType
        ) {
            getData()
        }
        return () => {
            _isMounted.current = false
        }
    }, [props.accountSlug, props.dateRange, props.filters, props.timeIdentifierType])

    const getData = async () => {
        setLoading(true)
        const requestTime = new Date().valueOf()
        _lastRequest.current = requestTime
        const timePeriods = ['today', 'yesterday', 'thisWeek', 'thisMonth']
        const timePeriodForMap = timePeriods.indexOf(props.dateRange.name) > -1 ? props.dateRange.name : 'custom'
        setTimePeriod(timePeriodForMap)
        const dateRangeLength = differenceInCalendarDays(props.dateRange.to, props.dateRange.from) + 1
        setTimePeriodLength(dateRangeLength)
        try {
            const todayIncluded = isBefore(startOfToday(), props.dateRange.to)
            const variables = {
                accountSlug: props.accountSlug,
                dateFrom: format(props.dateRange.from || new Date(), 'yyyy-MM-dd'),
                dateTo: format(props.dateRange.to || new Date(), 'yyyy-MM-dd'),
                comparisonType: 'percentage_growth',
                timeFilter: todayIncluded ? 'this_time_so_far' : null,
                delayType:
                    timePeriodForMap === 'thisMonth' ? 'months' : dateRangeLength <= 7 ? 'weeks' : 'previous_stretch',
                filters: props.filters,
                delay: 1,
                timeIdentifierType: props.timeIdentifierType,
            }
            const data = await statsService.getStats(queryConfig, variables)

            if (_isMounted.current && _lastRequest.current === requestTime) {
                setAvgCartSize(data.avgCartSize.totals?.value || '0')
                setRevenue(data.revenue.totals?.value || '0')
                setAvgCartValue(data.avgCartValue.totals?.value || '0')
                setCheckoutConvRate(data.checkoutConvRate.totals?.value || '0')
                setWebsiteConvRate(data.websiteConvRate.totals?.value || '0')
                setComparisonCartSize(data.avgCartSize.totals?.change || '-')
                setComparisonRevenue(data.revenue.totals?.change || '-')
                setComparisonCartValue(data.avgCartValue.totals?.change || '-')
                setComparisonCoConvRate(data.checkoutConvRate.totals?.change || '-')
                setComparisonWebConvRate(data.websiteConvRate.totals?.change || '-')
                setLoading(false)
            }
        } catch {
            props.replaceMessages(
                'server_error',
                'error',
                t('reports.dataLoadError'),
            )
            setLoading(false)
        }
    }

    const getPlusSign = (change: string) => {
        const value = Number(change.split('%')[0])
        return value > 0 ? '+' : ''
    }

    const formatChangeText = (stats: string, change: string) => {
        const value = Number(change.split('%')[0])
        return value >= 0
            ? t('reports.comparisonCopy.changeText', {
                  change: `${getPlusSign(change)}${change}`,
                  direction: infotipContentMap[stats].more,
              })
            : t('reports.comparisonCopy.changeText', { change, direction: infotipContentMap[stats].less })
    }

    const formatInfotipText = (stats: string, change: string) => {
        const comparisonCopy = getComparisonCopyMap()[timePeriod]
        const values = {
            start: comparisonCopy.infotipStart,
            change: formatChangeText(stats, change),
            item: infotipContentMap[stats].itemName,
            end: comparisonCopy.infotipEnd,
        }
        return (
            <>
                {timePeriod === 'yesterday' || timePeriod === 'custom'
                    ? t('reports.comparisonCopy.infotipPast', values)
                    : t('reports.comparisonCopy.infotipExpected', values)}
            </>
        )
    }

    const getComparisonText = () => {
        return timePeriod === 'thisMonth' ? 'MoM' : timePeriodLength <= 7 ? 'WoW' : 'prev. period'
    }

    const getComparisonLabel = () => {
        const comparisonText = getComparisonText()
        if (comparisonText === 'MoM') {
            return t('reports.comparisonCopy.monthOverMonth')
        }
        if (comparisonText === 'WoW') {
            return t('reports.comparisonCopy.weekOverWeek')
        }
        return t('reports.comparisonCopy.previousPeriodShort')
    }

    return (
        <ChartContainer style={{ marginTop: chartRowsGap, marginBottom: '2em' }}>
            {loading && <ChartDataLoader />}
            <ChartRow>
                <DataNumber
                    title={t('reports.sales.singleDataItems.revenue')}
                    value={Number(revenue) >= 10000 ? abbreviateNumber(Number(revenue), 1) : Number(revenue).toFixed(2)}
                    comparisonItem={t('reports.comparisonCopy.comparisonItem', {
                        change: `${getPlusSign(comparisonRevenue)}${comparisonRevenue}`,
                        period: getComparisonLabel(),
                    })}
                    comparisonItemColor={getComparisonColorFromPercentString(comparisonRevenue)}
                    infotipText={formatInfotipText('revenue', comparisonRevenue)}
                    metricInfotipText={infotipContentMap['revenue'].metricText}
                    tooltipType={getComparisonText()}
                    withCurrency
                    accountSlug={props.accountSlug}
                    compact
                    id="1"
                    nthChildStyle="margin-right: -2em"
                />
                <DataNumber
                    title={t('reports.sales.singleDataItems.avgOrderValue')}
                    value={Number(avgCartValue).toFixed(2)}
                    comparisonItem={t('reports.comparisonCopy.comparisonItem', {
                        change: `${getPlusSign(comparisonCartValue)}${comparisonCartValue}`,
                        period: getComparisonLabel(),
                    })}
                    comparisonItemColor={getComparisonColorFromPercentString(comparisonCartValue)}
                    infotipText={formatInfotipText('avgCartValue', comparisonCartValue)}
                    metricInfotipText={infotipContentMap['avgCartValue'].metricText}
                    withCurrency
                    accountSlug={props.accountSlug}
                    tooltipType={getComparisonText()}
                    compact
                    id="2"
                    nthChildStyle="margin-right: -1em"
                />
                <DataNumber
                    title={t('reports.sales.singleDataItems.avgItemsPerOrder')}
                    value={Math.round(Number(avgCartSize) * 10) / 10}
                    comparisonItem={t('reports.comparisonCopy.comparisonItem', {
                        change: `${getPlusSign(comparisonCartSize)}${comparisonCartSize}`,
                        period: getComparisonLabel(),
                    })}
                    comparisonItemColor={getComparisonColorFromPercentString(comparisonCartSize)}
                    infotipText={formatInfotipText('avgCartSize', comparisonCartSize)}
                    metricInfotipText={infotipContentMap['avgCartSize'].metricText}
                    infotipPosition="right"
                    tooltipType={getComparisonText()}
                    compact
                    id="3"
                    nthChildStyle="margin-right: 1em"
                />
                <DataNumber
                    title={t('reports.sales.singleDataItems.websiteConvRate')}
                    value={`${(Number(websiteConvRate) * 100).toFixed(2)}%`}
                    comparisonItem={t('reports.comparisonCopy.comparisonItem', {
                        change: `${getPlusSign(comparisonWebConvRate)}${comparisonWebConvRate}`,
                        period: getComparisonLabel(),
                    })}
                    comparisonItemColor={getComparisonColorFromPercentString(comparisonWebConvRate)}
                    infotipText={formatInfotipText('websiteConvRate', comparisonWebConvRate)}
                    metricInfotipText={infotipContentMap['websiteConvRate'].metricText}
                    infotipPosition="right"
                    tooltipType={getComparisonText()}
                    compact
                    id="4"
                />
                <DataNumber
                    title={t('reports.sales.singleDataItems.checkoutConvRate')}
                    value={`${(Number(checkoutConvRate) * 100).toFixed(2)}%`}
                    comparisonItem={t('reports.comparisonCopy.comparisonItem', {
                        change: `${getPlusSign(comparisonCoConvRate)}${comparisonCoConvRate}`,
                        period: getComparisonLabel(),
                    })}
                    comparisonItemColor={getComparisonColorFromPercentString(comparisonCoConvRate)}
                    infotipText={formatInfotipText('checkoutConvRate', comparisonCoConvRate)}
                    metricInfotipText={infotipContentMap['checkoutConvRate'].metricText}
                    infotipPosition="right"
                    tooltipType={getComparisonText()}
                    compact
                    id="5"
                    nthChildStyle="margin-right: -2em"
                />
            </ChartRow>
        </ChartContainer>
    )
}

export default withNavigation(SingleDataItems)
